import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotesIcon from '@mui/icons-material/Notes';

const SpecialReservationDialog = ({ 
  open, 
  onClose, 
  onSubmit, 
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    date: null,
    startTime: null,
    endTime: null
  });
  
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    console.log(`🔄 Field ${field} changed to:`, value);
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      console.log('📝 New form data:', newData);
      return newData;
    });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      console.log('🔄 Dialog opened, resetting form');
      setFormData({
        title: '',
        date: null,
        startTime: null,
        endTime: null
      });
      setErrors({});
    }
  }, [open]);

  const validateForm = () => {
    const newErrors = {};
    
    console.log("🔍 Validating form data:", formData);
    
    if (!formData.title || !formData.title.trim()) {
      newErrors.title = 'Titel ist erforderlich';
      console.log("❌ Title validation failed:", formData.title);
    }
    
    if (!formData.date) {
      newErrors.date = 'Datum ist erforderlich';
      console.log("❌ Date validation failed:", formData.date);
    }
    
    if (!formData.startTime) {
      newErrors.startTime = 'Startzeit ist erforderlich';
      console.log("❌ Start time validation failed:", formData.startTime);
    }
    
    if (!formData.endTime) {
      newErrors.endTime = 'Endzeit ist erforderlich';
      console.log("❌ End time validation failed:", formData.endTime);
    }
    
    if (formData.startTime && formData.endTime) {
      const start = new Date(formData.startTime);
      const end = new Date(formData.endTime);
      
      if (start >= end) {
        newErrors.endTime = 'Endzeit muss nach der Startzeit liegen';
        console.log("❌ Time range validation failed:", { start, end });
      }
    }
    
    console.log("🔍 Validation errors:", newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    console.log("🔍 Form data before validation:", formData);
    
    if (validateForm()) {
      // Format the data for submission
      const submissionData = {
        title: formData.title.trim(),
        date: formData.date,
        startTime: formData.startTime,
        endTime: formData.endTime,
        isSpecial: true,
        status: 'accepted', // Special reservations are auto-approved
        isPaid: true // Special reservations are considered paid
      };
      
      console.log("✅ Validation passed, submitting:", submissionData);
      onSubmit(submissionData);
    } else {
      console.log("❌ Validation failed, errors:", errors);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      date: null,
      startTime: null,
      endTime: null
    });
    setErrors({});
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #fff9c4 0%, #fff59d 100%)',
            border: '2px solid #ffd54f'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ffd54f 0%, #ffb300 100%)',
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontWeight: 'bold',
          fontSize: '1.5rem'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon sx={{ color: '#ff6f00', fontSize: '2rem' }} />
            <Typography variant="h5" component="span" sx={{ fontWeight: 'bold' }}>
              Sonderreservierung erstellen
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: '#333' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4, backgroundColor: '#fffde7' }}>
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3, 
              backgroundColor: '#e3f2fd',
              border: '1px solid #2196f3',
              '& .MuiAlert-icon': { color: '#1976d2' }
            }}
          >
            <Typography variant="body2">
              <strong>Hinweis:</strong> Sonderreservierungen werden automatisch genehmigt und blockieren die ausgewählten Zeiten für andere Buchungen.
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            {/* Title Field */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EventIcon sx={{ color: '#ff8f00' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333' }}>
                  Titel der Reservierung
                </Typography>
              </Box>
              <TextField
                fullWidth
                value={formData.title || ''}
                onChange={(e) => {
                  console.log('📝 Title input changed:', e.target.value);
                  handleChange('title', e.target.value);
                }}
                placeholder="z.B. Wartung, Schulung, Privattermin..."
                error={!!errors.title}
                helperText={errors.title}
                variant="outlined"
                autoComplete="off"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#ffa000' },
                    '&.Mui-focused fieldset': { borderColor: '#ff8f00' }
                  }
                }}
              />
            </Grid>

            {/* Date Field */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EventIcon sx={{ color: '#ff8f00' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333' }}>
                  Datum
                </Typography>
              </Box>
              <DatePicker
                value={formData.date}
                onChange={(value) => {
                  console.log('  Date changed:', value);
                  handleChange('date', value);
                }}
                format="dd.MM.yyyy"
                minDate={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.date,
                    helperText: errors.date,
                    autoComplete: "off",
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        '&:hover fieldset': { borderColor: '#ffa000' },
                        '&.Mui-focused fieldset': { borderColor: '#ff8f00' }
                      }
                    }
                  }
                }}
              />
            </Grid>

            {/* Start Time Field */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccessTimeIcon sx={{ color: '#ff8f00' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333' }}>
                  Startzeit
                </Typography>
              </Box>
              <TimePicker
                value={formData.startTime}
                onChange={(value) => {
                  console.log('⏰ Start time changed:', value);
                  handleChange('startTime', value);
                }}
                format="HH:mm"
                ampm={false}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.startTime,
                    helperText: errors.startTime,
                    autoComplete: "off",
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        '&:hover fieldset': { borderColor: '#ffa000' },
                        '&.Mui-focused fieldset': { borderColor: '#ff8f00' }
                      }
                    }
                  }
                }}
              />
            </Grid>

            {/* End Time Field */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccessTimeIcon sx={{ color: '#ff8f00' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333' }}>
                  Endzeit
                </Typography>
              </Box>
              <TimePicker
                value={formData.endTime}
                onChange={(value) => {
                  console.log('⏰ End time changed:', value);
                  handleChange('endTime', value);
                }}
                format="HH:mm"
                ampm={false}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.endTime,
                    helperText: errors.endTime,
                    autoComplete: "off",
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        '&:hover fieldset': { borderColor: '#ffa000' },
                        '&.Mui-focused fieldset': { borderColor: '#ff8f00' }
                      }
                    }
                  }
                }}
              />
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          backgroundColor: '#fff8e1',
          borderTop: '1px solid #ffcc02',
          gap: 2
        }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: '#ff8f00',
              color: '#ff8f00',
              '&:hover': {
                borderColor: '#f57c00',
                backgroundColor: '#fff3e0'
              }
            }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <StarIcon />}
            sx={{
              background: 'linear-gradient(135deg, #ffd54f 0%, #ffb300 100%)',
              color: '#333',
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #ffcc02 0%, #ffa000 100%)',
              },
              '&:disabled': {
                background: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            {loading ? 'Wird erstellt...' : 'Sonderreservierung erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default SpecialReservationDialog;